import React from "react";
import { motion } from "framer-motion";
import ExperienceItem from "../components/sections/experience/ExperienceItem";

// Add these category constants at the top of the file
const CATEGORIES = {
  EMPLOYMENT: {
    name: "Employment",
    color: "bg-blue-400",
    textColor: "text-blue-600",
    bgColor: "bg-blue-50",
  },
  COLLEGE: {
    name: "College Activity",
    color: "bg-green-400",
    textColor: "text-green-600",
    bgColor: "bg-green-50",
  },
  VOLUNTEER: {
    name: "Volunteer",
    color: "bg-purple-400",
    textColor: "text-purple-600",
    bgColor: "bg-purple-50",
  },
};

const Experience = () => {
  const experiences = [
    {
      title: "Head of IT",
      company: "Nova Sky Stories",
      date: "Jun 2026 - Present",
      location: "Boulder, CO",
      category: CATEGORIES.EMPLOYMENT,
      keywords: [
        "IT Strategy",
        "Vendor Management",
        "AI Adoption",
        "Facility Buildouts",
      ],
      technologies: [
        "Microsoft 365",
        "Google Workspace",
        "Slack",
        "Notion",
        "SCIM",
        "SSO",
        "Apple Developer",
      ],
      points: [
        "Promoted to lead Corporate IT after eight months building the function from zero; now own strategy, budget, vendor relationships, and a 30+ SaaS portfolio serving a global workforce across three countries; established IT metrics and report quarterly to leadership.",
        "Architected and deployed Nova HelpHub, a company-wide intranet and IT ticketing layer integrating Microsoft, Google, Nova SSO Applications, Slack and more via SCIM behind automated provisioning scripts, eliminating manual account creation and cutting onboarding setup time by over 80%.",
        "Drove AI adoption strategy company-wide: ran a comprehensive AI tools audit, co-authored the company’s first AI usage policy.",
        "Negotiated and managed vendor contracts including a 23-seat Maxon license ($25k/year), 150-seat Slack/Notion/Google contracts; established Apple Developer and federated Microsoft 365 ecosystems from scratch.",
        "Led the Boulder warehouse and Berkeley California IT & security buildouts end to end: sourced ISP, designed network topology, deployed access control and badge systems, and coordinated vendor installation to deliver a fully operational facility on schedule and under budget.",
      ],
    },
    {
      title: "Software Engineering & IT Operations Lead",
      company: "Nova Sky Stories",
      date: "Oct 2025 - Jun 2026",
      location: "Boulder, CO",
      category: CATEGORIES.EMPLOYMENT,
      keywords: [
        "Full-Stack Development",
        "Operations Platform",
        "Platform Migration",
        "RBAC",
      ],
      technologies: [
        "React",
        "Node.js",
        "PostgreSQL",
        "Railway",
        "Neon",
        "Resend",
        "Cloudflare",
      ],
      points: [
        "Built and shipped Nova Backbone solo after two failed attempts at off-the-shelf tooling, first Notion, then a large ClickUp deployment, neither of which could accurately model how drone shows get planned. Shipped a proof of concept on my own initiative, won over production managers and leadership, and turned it into a funded multi-month project priority.",
        "Grew it into a 13-module operations platform on React, Node.js, PostgreSQL, and Railway, now serving as the company-wide system of record for 250+ show projects per year, 550+ aviation permits, a ~35,000-unit drone fleet across 10 countries, plus crew scheduling, travel, logistics, client management, and analytics.",
        "Selected and implemented Railway and Neon as the company’s application and database platform, replacing an estate split between hundreds of loosely governed Google Cloud projects with opaque billing and a single DigitalOcean droplet running dozens of apps, databases, and environment. Replaced manual SSH deploys with Git-native delivery, UI-managed permissions, per-service cost attribution, and blast-radius isolation that removed a company-wide single point of failure.",
        "Built a synthetic data generator that seeds dev and staging with realistic, referentially consistent records across shows, permits, crew, travel, and fleet inventory, enabling full-scale testing and safe external demos without touching production data.",
        "Developed a milestone templating engine that generates 140+ tracked milestones per show project, including per-show-date execution milestones for multi-date runs, with operator-triggered regeneration and bulk reset.",
        "Built the notification layer, combining transactional email via Resend with an in-app notification center for record changes, upcoming actions, and slipped or overdue milestones. Routed date slips to affected parties for resolution instead of silently recomputing downstream dates, preserving manual scheduling decisions made for operational reasons.",
        "Designed a six-dimension health model per show, spanning production, permitting, design, scheduling, shipping, and overall status, with automatic rollups from underlying records and per-field manual override to surface at-risk shows on a triage dashboard.",
        "Implemented a multi-step approval workflow engine for aviation permitting, moving reviews from Expert to Manager to Routine Updates, with reviewer queues, overdue escalation, automated health scoring, and region-scoped views for EU and US operations.",
        "Designed hierarchical role-based access control spanning 18 roles and 14 permission groups, with section-level defaults, granular per-permission overrides, and a view-as-role mode for verifying access without maintaining test accounts.",
        "Built global fleet planning with time-series inventory across transit and repair pools in 10 international locations, visualized as stacked time-series charts and an interactive geospatial map with a date scrubber, backed by bulk CSV ingest with template generation and duplicate detection.",
        "Shipped a configurable data grid with user-defined saved views, column reordering and resizing, inline editing, and multi-field filtering, backed by PostgreSQL audit trails and rollback.",
        "Ran the entire IT function solo on the same timelines, and launched the revamped production website, resolving third-party integration issues including Osano cookie consent and Instagram embeds, and coordinating Cloudflare cache and DNS invalidation for a clean public launch.",
      ],
    },
    {
      title: "USA Climbing Volunteer Bouldering/TR/Lead Judge",
      company: "USA Climbing",
      date: "October 2024 - Ongoing",
      location: "Central & Northern CO (Region 42)",
      category: CATEGORIES.VOLUNTEER,
      keywords: ["Competition Judging", "Indoor Rock Climbing", "Safety"],
      points: [
        "Certified Level 2 Bouldering/Top Rope/ Lead ClimbingJudge responsible for ensuring safety and rule compliance at USA Climbing competitions.",
        "Evaluate climber attempts according to official rules and standards, maintaining competition integrity.",
        "Collaborate with other judges and event staff to facilitate smooth competition operations.",
        "Contribute to the climbing community by supporting youth and adult competitive climbing events.",
      ],
    },
    {
      title: "Staff Software Engineer - Mobile/Web & IT Operations ",
      company: "Scythe Robotics",
      date: "Jan 2023 - Jan 2025",
      location: "Longmont, CO",
      category: CATEGORIES.EMPLOYMENT,
      keywords: [
        "Mobile Development",
        "Web Development",
        "Team Leadership",
        "UX Design",
      ],
      technologies: [
        "Ionic",
        "React",
        "TypeScript",
        "JavaScript",
        "GraphQL",
        "Zustand",
        "GitLab CI/CD",
        "AWS",
        "Docker",
        "Firebase",
        "Push Notifications",
        "Capacitor",
      ],
      points: [
        "Architected and led development of a mission-critical cross-platform Ionic React application enabling real-time tracking, control, and diagnostics for hundreds of autonomous mowers across the U.S.",
        "Built full-stack features using TypeScript, Zustand, GraphQL, and RESTful APIs, ensuring high-availability and performance under fleet-scale loads.",
        "Designed and deployed secure, device-agnostic OAuth & 2FA authentication flows to accommodate operators in variable field conditions, enhancing accessibility and enablement of non-traditional login mechanisms.",
        "Streamlined provisioning and infrastructure automation, reducing developer onboarding time by 80% and slashing hardware costs by 66% via scalable, scripted setups and strategic sourcing.",
        "Led IT operations across 4+ office locations and remote test sites, improving network reliability, access controls, and monitoring systems.",
        "Drove cross-functional alignment between Product, Engineering, Manufacturing, and IT, unifying system architecture and accelerating feedback loops across deployment, support, and debugging.",
        "Mentored engineers and championed internal tooling improvements, raising developer velocity and code quality across mobile, robot software, and infrastructure teams.",
     ],
    },
    {
      title: "Head of Information Technology",
      company: "Scythe Robotics",
      date: "Sep 2023 - Jan 2025",
      location: "Longmont, CO",
      category: CATEGORIES.EMPLOYMENT,
      keywords: [
        "IT Infrastructure",
        "Process Automation",
        "Cost Optimization",
        "System Administration",
      ],
      technologies: [
        "Python",
        "Bash Scripting",
        "MDM (Mobile Device Management) Solutions",
        "Network Infrastructure",
        "Cloud Services",
        "Ubiquity Unifi",
        "Aruba Instant On",
        "Atlassian",
        "Jira",
        "YoDeck Screen Management",
        "Meeting Room Conferencing Solutions",
        "Tailscale",
        "Google Workspace",
        "GitLab",
      ],
      points: [
        "Led IT operations across 4+ office locations and remote test sites, improving network reliability, access controls, and monitoring systems.",
        "Automated developer workstation and production line machine setup, reducing provisioning time from 3+ days to a few hours.",
        "Optimized IT expenditures, reducing hardware costs by 66% while upgrading to higher-spec machines.",
        "Drove cross-functional alignment between Product, Engineering, Manufacturing, and IT, unifying system architecture and accelerating feedback loops across deployment, support, and debugging.",
        "Implemented network and system upgrades that improved office connectivity, security, and developer productivity.",
      ],
    },
    {
      title: "Software Developer - US Claims",
      company: "Liberty Mutual Insurance",
      date: "Jul 2021 - Jan 2023",
      location: "Boston, MA",
      category: CATEGORIES.EMPLOYMENT,
      keywords: [
        "Full Stack Development",
        "Team Mentoring",
        "Employee Onboarding",
        "System Architecture",
        "API Design",
        "CI/CD",
        "AWS",
        "JavaScript",
        "TypeScript",
        "GraphQL",
        "Jenkins",
        "Bamboo",
        "BitBucket",
        "Jira",
        "Confluence",
      ],
      technologies: [
        "React",
        "Node.js",
        "AWS Lambda",
        "GraphQL",
        "RESTful APIs",
      ],
      points: [
        "Developed and maintained a full-stack React + Node.js web application to streamline property and auto claim workflows used by thousands of policyholders daily.",
        "Onboarded and mentored a newly formed claims development team, accelerating ramp-up and improving team velocity.",
        "Migrated legacy systems to serverless AWS Lambda APIs using REST and GraphQL, cutting infrastructure maintenance and improving scalability.",
        "Optimized CI/CD pipelines and internal tooling, boosting release efficiency by 30% and reducing deployment friction across teams.",
      ],
    },
    {
      title: "Associate Software Developer - US Architecture",
      company: "Liberty Mutual Insurance",
      date: "Feb 2020 - Jul 2021",
      location: "Boston, MA",
      category: CATEGORIES.EMPLOYMENT,
      keywords: [
        "Software Patterns",
        "DevOps",
        "Documentation",
        "Best Practices",
        "Automated Migrations",
        "System Architecture",
        "API Design",
        "CI/CD",
        "AWS",
        "Bamboo",
        "BitBucket",
        "Jira",
        "Confluence",
      ],
      technologies: [
        "Node.js",
        "React",
        "Java",
        "AWS Lambda",
        "Docker",
        "Kubernetes",
      ],
      points: [
        "Developed reusable software code patterns adopted by over 1,000 internal developers, establishing best practices for Node.js, React, Java, and AWS Lambda.",
        "Advanced an internal self-service ecosystem that enabled engineers to generate fully deployed, functional applications via a web interface in minutes.",
        "Contributed to a company-wide Kubernetes migration, improving scalability, deployment speed, and operational resilience.",
        "Standardized coding practices and documentation to improve onboarding speed and maintainability across multiple teams.",
      ],
    },
    {
      title: "Software Developer - TechStart",
      company: "Liberty Mutual Insurance",
      date: "Jun 2019 - Feb 2020",
      location: "Boston, MA",
      category: CATEGORIES.EMPLOYMENT,
      keywords: [
        "Web Development",
        "CI/CD",
        "Process Improvement",
        "SMS Integration",
        "Project Management",
      ],
      technologies: [
        "React",
        "Bamboo",
        "SMS Integration",
        "Twilio",
        "AWS",
        "Node.js",
        "Python",
        "Jira",
        "Confluence",
        "BitBucket",
      ],
      points: [
        "Led a React-based platform project that optimized SMS messaging workflows for customer care representatives communicating with policyholders.",
        "Designed a Bamboo CI/CD pipeline enhancement that reduced commit processing time by 10 minutes per commit, accelerating development cycles for both my team and all future cohorts.",
        ],
    },
    {
      title: "Software Development Intern",
      company: "Liberty Mutual Insurance",
      date: "May 2018 - Aug 2018",
      location: "Boston, MA",
      category: CATEGORIES.EMPLOYMENT,
      keywords: ["Full Stack Development", "Agile Development"],
      technologies: ["Django", "Python", "Jinja", "AWS", "Bamboo", "BitBucket"],
      points: [
        "Developed a full-stack web application using Django, Jinja, and Python, earning 1st place in a company-wide software innovation challenge.",
        "Expanded a Python-based application to support additional insurance product lines, improving operational efficiency.",
        "Collaborated with cross-functional teams in an Agile environment to enhance software quality and deliverables.",
      ],
    },
    {
      title: "Instructional Aide",
      company: "Penn State College of IST",
      date: "Aug 2018 - Dec 2018",
      location: "University Park, PA",
      category: CATEGORIES.COLLEGE,
      keywords: ["Teaching", "Mentoring", "Programming Concepts"],
      technologies: ["Java", "Object-Oriented Programming"],
      points: [
        "Provided mentorship and academic support to students in an intermediate Java programming course.",
        "Facilitated lab sessions, assisting students in debugging, object-oriented programming, and software architecture concepts.",
      ],
    },
    {
      title: "Resident Assistant - IST Special Living Option",
      company: "Penn State Residence Life",
      date: "Aug 2016 - May 2019",
      location: "University Park, PA",
      category: CATEGORIES.COLLEGE,
      keywords: [
        "Community Leadership",
        "Event Planning",
        "Crisis Management",
        "Student Mentoring",
      ],
      points: [
        "Led a specialized residential community of 62 students passionate about Information Sciences and Technology (IST), fostering collaboration and engagement.",
        "Developed and organized IST-focused events such as hackathons, coding workshops, cybersecurity challenges, and industry networking sessions.",
        "Mentored students in technical projects, resume building, and internship applications, facilitating career development opportunities.",
        "Handled crisis situations in a 440-resident hall, coordinating emergency responses, conflict resolution, and wellness support.",
        "Implemented initiatives to bridge the gap between academic coursework and real-world applications, connecting students with faculty, campus resources, and tech organizations.",
      ],
    },
    {
      title: "Night Auditor & Front Desk Agent",
      company: "Hampton Inn & Suites",
      date: "Jun 2016 - Aug 2017",
      location: "State College, PA",
      category: CATEGORIES.EMPLOYMENT,
      points: [
        "Implemented process improvements that reduced nightly audit times by 20 minutes through scripting and workflow optimizations.",
        "Trained and mentored new hires on hotel management systems, enhancing operational efficiency and customer satisfaction.",
      ],
    },
    {
      title: "IT Officer",
      company: "Penn State 3D Printing Club",
      date: "Aug 2015 - May 2017",
      location: "University Park, PA",
      category: CATEGORIES.EMPLOYMENT,
      keywords: ["3D Printing", "IoT", "Raspberry Pi"],
      technologies: [
        "Network Infrastructure",
        "IoT",
        "Repetier Host",
        "Cura Slicer",
        "OctoPrint",
        "MakerBot",
        "Prusa",
        "Creality",
        "FlashForge",
        "Formlabs",
        "Ultimaker",
      ],
      points: [
        "Designed and deployed the club's first networked 3D printer farm, allowing remote job submission and real-time monitoring.",
        "Implemented a server-based queuing system that optimized print scheduling and increased equipment utilization by 40%.",
        "Integrated IoT-based sensor monitoring for print quality tracking, reducing failed prints and improving operational efficiency.",
        "Managed IT infrastructure, providing technical support for student-led 3D printing projects and research initiatives.",
      ],
    },
    {
      title: "Webmaster",
      company: "Penn State South Halls Residence Association",
      date: "Aug 2015 - May 2016",
      location: "University Park, PA",
      category: CATEGORIES.EMPLOYMENT,
      points: [
        "Developed and maintained the organization's website, ensuring up-to-date information and improved user engagement.",
      ],
    },
    {
      title: "Tech Team Member",
      company: "Penn State Red Cell Analytics Lab",
      date: "Aug 2015 - May 2017",
      location: "University Park, PA",
      category: CATEGORIES.EMPLOYMENT,
      points: [
        "Conducted data-driven security analytics and developed software solutions for intelligence applications.",
      ],
    },
    {
      title: "IT Intern",
      company: "Everett Cash Mutual Insurance Company",
      date: "Jun 2015 - Aug 2015",
      location: "Everett, PA",
      category: CATEGORIES.EMPLOYMENT,
      points: [
        "Performed data verification and assisted in migrating legacy databases from FoxPro to WebDev 20 Analysis.",
        "Provided IT support and assisted in the validation and accuracy of data entry during conversion processes.",
      ],
    },
    {
      title: "Farm Laborer, Sales, and Technology Consultant",
      company: "Brush Creek Evergreens",
      date: "November 2011 - 2017",
      location: "Breezewood, PA",
      category: CATEGORIES.EMPLOYMENT,
      keywords: [
        "Agriculture",
        "Sales Operations",
        "Network Infrastructure",
        "Payment Systems",
      ],
      technologies: [
        "Point-to-Point Wireless",
        "Square POS",
        "Wireless Networks",
      ],
      points: [
        "Worked year-round in farm operations, assisting with planting, trimming, fertilizing, and maintaining Christmas trees to ensure high-quality growth.",
        "Helped onboard and train new employees, teaching best practices for tree care, customer interactions, and sales processes.",
        "Played a key role in seasonal sales operations, assisting with inventory management, customer service, and winter retail logistics.",
        "Identified inefficiencies in the business's payment system and implemented a custom-built point-to-point wireless network, allowing the company to adopt Square for credit card processing, saving thousands annually in transaction fees.",
        "Designed and deployed an outdoor wireless network, extending connectivity to remote retail areas despite infrastructure limitations, an implementation that remains in use over a decade later.",
      ],
    },
  ];

  // Track displayed years to ensure each appears only once
  const displayedYears = new Set();

  // Helper function to get year from date
  const getYear = (date) => {
    const fullYear = date.split(" - ")[0].split(" ")[1] || date.split(" ")[1];
    return `'${fullYear.slice(-2)}`; // Convert to 'YY format
  };

  // Pre-process experiences to find last item of each year
  const lastItemOfYear = experiences.reduce((acc, exp, idx) => {
    const year = getYear(exp.date);
    const nextExp = experiences[idx + 1];
    const nextYear = nextExp ? getYear(nextExp.date) : null;

    if (year !== nextYear) {
      acc[year] = idx;
    }
    return acc;
  }, {});

  return (
    <div className="container mx-auto px-4 py-16">
      <h1 className="text-4xl font-bold text-gray-800 mb-12">
        Professional Experience & Community Involvement
      </h1>
      <div className="relative">
        <div className="absolute left-12 top-0 h-full w-1 bg-primary-400"></div>
        <div className="space-y-8 pl-20">
          {experiences.map((experience, index) => {
            const year = getYear(experience.date);
            const showYear = lastItemOfYear[year] === index;

            return (
              <div key={index} className="relative flex flex-col items-start">
                <div className="w-full">
                  <ExperienceItem
                    {...experience}
                    category={experience.category}
                  />
                  {showYear && (
                    <div className="absolute -left-28 top-4 flex items-center">
                      <span className="text-gray-500 text-sm">{year}</span>
                      <div className="w-16 h-1 bg-primary-400 ml-2"></div>
                    </div>
                  )}
                  <div className="absolute -left-4 top-4 w-8 h-8 bg-primary-400 rounded-full"></div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Experience;
